import { Component, HostListener, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ProjectState,
  STAGE_META,
  StageMeta,
  formatStageState,
  stageMetaFor,
} from '../../../dtos/project-state';

export type StageStatus = 'finished' | 'active' | 'current-page' | 'disabled';

interface PopoverData {
  stage: ProjectState;
  meta: StageMeta;
  x: number;
  y: number;
  status: StageStatus;
  statusLabel: string;
}

@Component({
  selector: 'app-project-stage-nav',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './project-stage-nav.component.html',
  styleUrls: ['./project-stage-nav.component.scss'],
  host: { class: 'block w-full' },
})
export class ProjectStageNavComponent implements OnDestroy {
  @Input({ required: true }) projectId!: string;
  @Input({ required: true }) projectState!: string;
  @Input({ required: true }) currentPageStage!: ProjectState;
  @Input() userRole: 'PI' | 'RESEARCHER' | 'NONE' = 'NONE';

  readonly ProjectState = ProjectState;
  readonly stages = STAGE_META;

  popover: PopoverData | null = null;
  private popoverHideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private router: Router) {}

  metaFor(stage: ProjectState): StageMeta {
    return stageMetaFor(stage) as StageMeta;
  }

  labelFor(stage: ProjectState): string {
    return this.metaFor(stage).label;
  }

  numberFor(stage: ProjectState): string {
    return this.metaFor(stage).number;
  }

  descriptionFor(stage: ProjectState): string {
    return this.metaFor(stage).description;
  }

  getStageStatus(stage: ProjectState): StageStatus {
    if (!this.projectState) {
      return 'disabled';
    }

    const currentIdx = this.projectStageIndex();
    const targetIdx = this.stageIndex(stage);

    if (currentIdx === -1 || targetIdx === -1) {
      return 'disabled';
    }

    if (stage === this.currentPageStage) {
      return 'current-page';
    }

    if (targetIdx < currentIdx) {
      return 'finished';
    }

    if (this.isSetupOrIngestion(stage)) {
      const pairStartIdx = this.stageIndex(ProjectState.SETUP);
      const pairEndIdx = this.stageIndex(ProjectState.INGESTION);
      if (currentIdx >= pairStartIdx && currentIdx <= pairEndIdx) {
        return 'active';
      }
      return 'disabled';
    }

    if (targetIdx > currentIdx) {
      return 'disabled';
    }

    return 'active';
  }

  statusLabel(stage: ProjectState): string {
    const status = this.getStageStatus(stage);
    switch (status) {
      case 'active':
        return 'In Progress';
      case 'current-page':
        return 'Current Page';
      case 'finished':
        return 'Completed';
      case 'disabled':
      default:
        return 'Locked';
    }
  }

  isDisabled(stage: ProjectState): boolean {
    return this.getStageStatus(stage) === 'disabled';
  }

  stageBtnClass(stage: ProjectState): string {
    const status = this.getStageStatus(stage);
    const base =
      'group flex items-center gap-3 px-3 py-2.5 border-b-3 transition-all duration-200 w-full text-left min-h-18 max-h-18';
    switch (status) {
      case 'current-page':
        return `${base} border-secondary/80 bg-secondary/10 text-secondary font-semibold shadow-sm`;
      case 'active':
        return `${base} border-secondary/80 text-secondary font-semibold`;
      case 'finished':
        return `${base} border-emerald-500/80 hover:bg-emerald-50/60 text-slate-800`;
      case 'disabled':
      default:
        return `${base} border-transparent text-slate-400 cursor-default`;
    }
  }

  numberBadgeClass(stage: ProjectState): string {
    const status = this.getStageStatus(stage);
    const base =
      'inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-md text-[11px] font-extrabold leading-none flex-shrink-0';
    switch (status) {
      case 'finished':
        return `${base} bg-gradient-to-br from-emerald-600 to-emerald-500 text-white shadow-sm`;
      case 'active':
      case 'current-page':
        return `${base} bg-gradient-to-br from-secondary to-hover text-white shadow-sm`;
      case 'disabled':
      default:
        return `${base} bg-slate-200 text-slate-500`;
    }
  }

  statusDotClass(stage: ProjectState): string {
    const status = this.getStageStatus(stage);
    const base = 'inline-block w-1.5 h-1.5 rounded-full flex-shrink-0';
    switch (status) {
      case 'finished':
        return `${base} bg-emerald-500`;
      case 'current-page':
      case 'active':
        return `${base} bg-secondary`;
      case 'disabled':
      default:
        return `${base} bg-slate-300`;
    }
  }

  labelClass(stage: ProjectState): string {
    const status = this.getStageStatus(stage);
    const base = 'text-sm font-bold leading-tight break-words';
    return status === 'disabled' ? `${base} text-slate-400` : base;
  }

  statusTextClass(stage: ProjectState): string {
    const status = this.getStageStatus(stage);
    const base = 'text-[10px] font-semibold uppercase tracking-wide leading-none mt-0.5';
    switch (status) {
      case 'finished':
        return `${base} text-emerald-600`;
      case 'current-page':
      case 'active':
        return `${base} text-secondary`;
      case 'disabled':
      default:
        return `${base} text-slate-400`;
    }
  }

  onStageTabClick(stage: ProjectState): void {
    if (this.isDisabled(stage)) return;
    const route = this.metaFor(stage).routeSegment;
    if (route) {
      this.router.navigate(['/projects', this.projectId, route]);
    } else {
      this.router.navigate(['/projects', this.projectId]);
    }
    this.hidePopover();
  }

  showPopover(stage: ProjectState, event: MouseEvent): void {
    if (this.popoverHideTimeout) {
      clearTimeout(this.popoverHideTimeout);
      this.popoverHideTimeout = null;
    }
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.popover = {
      stage,
      meta: this.metaFor(stage),
      x: rect.left + rect.width / 2,
      y: rect.bottom + 10,
      status: this.getStageStatus(stage),
      statusLabel: this.statusLabel(stage),
    };
  }

  scheduleHidePopover(): void {
    if (this.popoverHideTimeout) {
      clearTimeout(this.popoverHideTimeout);
    }
    this.popoverHideTimeout = setTimeout(() => {
      this.popover = null;
      this.popoverHideTimeout = null;
    }, 0);
  }

  keepPopover(): void {
    if (this.popoverHideTimeout) {
      clearTimeout(this.popoverHideTimeout);
      this.popoverHideTimeout = null;
    }
  }

  hidePopover(): void {
    this.popover = null;
  }

  popoverPillClass(status: StageStatus): string {
    const base = 'inline-flex items-center gap-1 rounded-md py-0.5 px-2 text-[11px] font-medium';
    const variants: Record<StageStatus, string> = {
      finished: 'bg-emerald-600/10 text-emerald-700',
      'current-page': 'bg-secondary/10 text-secondary',
      active: 'bg-secondary/10 text-secondary',
      disabled: 'bg-slate-100 text-slate-400',
    };
    return `${base} ${variants[status]}`;
  }

  popoverPillDotClass(status: StageStatus): string {
    const base = 'w-1.5 h-1.5 rounded-full inline-block';
    const variants: Record<StageStatus, string> = {
      finished: 'bg-emerald-600',
      'current-page': 'bg-secondary',
      active: 'bg-secondary',
      disabled: 'bg-slate-400',
    };
    return `${base} ${variants[status]}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.popover) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.tooltip-card') && !target.closest('.stage-nav-item')) {
      this.popover = null;
    }
  }

  ngOnDestroy(): void {
    if (this.popoverHideTimeout) {
      clearTimeout(this.popoverHideTimeout);
      this.popoverHideTimeout = null;
    }
  }

  private isSetupOrIngestion(stage: ProjectState): boolean {
    return stage === ProjectState.SETUP || stage === ProjectState.INGESTION;
  }

  private stageIndex(stage: ProjectState): number {
    return this.stages.findIndex((s) => s.state === stage);
  }

  private projectStageIndex(): number {
    return this.stageIndex(this.projectState as ProjectState);
  }

  formatStage(state?: string): string {
    return formatStageState(state);
  }
}
